/**
 *  @class
 *  @function ModalDialog
 */
if (!customElements.get("modal-dialog")) {
	/**
	 * Cooldown days after closing an auto-show popup (data-popup; currently only the subscribe popup in sections/overlay-popup.liquid).
	 * Confirmed with the requester on 2026-09-17: after one close, don't bother the user again for 14 days; after a successful subscription, never show again.
	 */
	const POPUP_COOLDOWN_DAYS = 14;

	/**
	 * Besides waiting out data-delay, the auto popup also waits for the user's first real interaction. Any of these four inputs makes the browser stop tracking LCP.
	 * Deliberately excludes scroll: restoring scroll position on reload and anchor jumps both dispatch scroll, but they are not user input.
	 */
	const POPUP_INTERACTION_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"];

	/** When the interaction happens after data-delay has elapsed, buffer this long before showing; see scheduleAutoShow() */
	const POPUP_AFTER_INTERACTION_MS = 1000;

	/** localStorage key for the popup A/B group; ga4-subscribe-result.liquid reads the same key */
	const POPUP_AB_KEY = "soulog_popup_ab";

	class ModalDialog extends HTMLElement {
		constructor() {
			super();

			this.age_verification = this.classList.contains("age-verification-modal");
			this.id = this.getAttribute("id");
			this.delay = parseInt(this.dataset.delay, 10) * 1000;
			// Phones (< 768px, the theme's main breakpoint) can use their own delay from the section setting
			// (sections/overlay-popup.liquid popup_delay_mobile); without it, the desktop delay applies.
			const mobileDelay = parseInt(this.dataset.delayMobile, 10);
			if (!Number.isNaN(mobileDelay) && window.matchMedia && window.matchMedia("(max-width: 767px)").matches) {
				this.delay = mobileDelay * 1000;
			}
			this.popup = this.dataset.popup;
			this.section_id = this.dataset.sectionId;
			this.disabled = this.getAttribute("disabled") != undefined;
			this.button = this.querySelector('[id^="ModalClose-"]');

			this.button.addEventListener("click", this.hide.bind(this));

			if (!this.disabled) {
				this.addEventListener("keyup", (event) => {
					// Chrome autofill fires keyup with no event.code, which used to throw here
					if (String(event.code || "").toUpperCase() === "ESCAPE") {
						this.hide();
					}
				});
				this.addEventListener("click", (event) => {
					if (event.target.nodeName === "MODAL-DIALOG") this.hide();
				});
			}
			// The original check was `this.delay && this.delay > 0`, so when the admin set Pop-up delay to 0
			// the popup never showed at all -- but the schema's min for delay is 0, and 0 means
			// "no delay, show immediately", not "disable the popup" (to disable it, remove the section in the admin).
			// Use data-popup as the gate instead: only sections/overlay-popup.liquid carries this attribute,
			// so click-triggered modal-dialogs (video / size chart / #SubscribePopup) are unaffected.
			if (this.popup && !Number.isNaN(this.delay)) {
				// A/B test (title x delay). Runs before scheduling so the assigned delay is the one used.
				this.applyAbTest();
				// People who subscribed successfully must never be bothered again: first settle "whether the previous submission redirected back successfully", then decide whether to show this time.
				this.watchSubscribeSuccess();
				if (this.shouldAutoShow()) {
					this.schedulePreload();
					this.scheduleAutoShow();
				}
			}
			if (this.age_verification) {
				if (!this.getCookie()) {
					this.show();
				}
				this.button.addEventListener("click", this.setCookie.bind(this));
			}
			if (Shopify.designMode) {
				this.moved = true;
				this.onSectionSelectBound = (event) => {
					if (event.detail.sectionId === this.section_id) {
						this.show();
					}
				};
				this.onSectionDeselectBound = (event) => {
					if (event.detail.sectionId === this.section_id) {
						this.hide();
					}
				};
				document.addEventListener("shopify:section:select", this.onSectionSelectBound);
				document.addEventListener("shopify:section:deselect", this.onSectionDeselectBound);
			}
		}

		disconnectedCallback() {
			if (this.onSectionSelectBound) {
				document.removeEventListener("shopify:section:select", this.onSectionSelectBound);
			}
			if (this.onSectionDeselectBound) {
				document.removeEventListener("shopify:section:deselect", this.onSectionDeselectBound);
			}
		}

		connectedCallback() {
			if (this.moved) return;
			this.moved = true;
			document.body.appendChild(this);
		}

		/**
		 * Popup A/B test, configured in the section's "A/B 测试" settings (sections/overlay-popup.liquid).
		 * Only present when the admin filled in a test name; otherwise this returns immediately.
		 *
		 * Two independent dimensions, each randomized separately (a 3 x 3 grid when both have 3 arms):
		 *   title: original heading / new title 1 / new title 2 (blank ones are left out); each new title
		 *          may come with its own submit-button text, swapped together as one copy version
		 *   delay: the seconds listed in data-ab-delays, applied on desktop AND mobile, floor 3s;
		 *          the "wait for a real interaction" gate in scheduleAutoShow() still applies to every arm.
		 * Comparing titles = add up a row, comparing delays = add up a column; each uses all traffic.
		 *
		 * The group is kept in localStorage per test name, so a visitor sees the same version on every visit;
		 * renaming the test starts a new round with everyone re-assigned.
		 *
		 * The denominator must be "visitors assigned", not "popups shown": in a later-delay arm more people
		 * leave before it appears, so per-view rates would be biased. Hence popup_ab_assign on every page view,
		 * before and independent of whether the popup will show.
		 */
		applyAbTest() {
			// What watchSubscribeSuccess() snapshots at submit; null = no test running on this page
			this.abParams = null;
			const name = String(this.dataset.abTest || "")
				.toLowerCase()
				.replace(/[^a-z0-9-]+/g, "-")
				.replace(/^-+|-+$/g, "");
			// No test (any more): drop the stored group so nothing later stamps a finished round's params
			const endTest = () => {
				try {
					window.localStorage.removeItem(POPUP_AB_KEY);
				} catch (e) {}
			};
			if (!name) return endTest();

			const titles = [{ label: "original", text: null }];
			// getAttribute, not dataset: dataset only camelCases "-letter", so data-ab-title-1 is dataset["abTitle-1"]
			["1", "2"].forEach((n) => {
				const text = this.getAttribute("data-ab-title-" + n);
				const button = this.getAttribute("data-ab-button-" + n);
				if (text && text.trim()) {
					titles.push({ label: "new_" + n, text: text.trim(), button: button ? button.trim() : "" });
				}
			});
			const delays = [];
			String(this.dataset.abDelays || "")
				.split(/[,，\s]+/)
				.forEach((part) => {
					let seconds = parseInt(part, 10);
					if (Number.isNaN(seconds)) return;
					seconds = Math.min(30, Math.max(3, seconds));
					if (delays.indexOf(seconds) === -1) delays.push(seconds);
				});
			if (titles.length < 2 && delays.length < 2) return endTest();

			const pick = (n) => Math.floor(Math.random() * n);
			let group = null;
			let preview = false;
			// QA preview: ?popup_ab=<title index>-<delay index>, e.g. 2-0. Not stored, not reported.
			try {
				const forced = new URLSearchParams(window.location.search).get("popup_ab");
				const match = forced && forced.match(/^(\d+)-(\d+)$/);
				if (match) {
					group = { t: parseInt(match[1], 10), d: parseInt(match[2], 10) };
					preview = true;
				}
			} catch (e) {}
			if (!group) {
				try {
					group = JSON.parse(window.localStorage.getItem(POPUP_AB_KEY) || "null");
				} catch (e) {
					group = null;
				}
				if (!group || group.name !== name) group = { name: name };
				// A dimension the admin later removed / shrank gets re-drawn rather than pointing at nothing
				if (!(group.t >= 0 && group.t < titles.length)) group.t = pick(titles.length);
				if (!(group.d >= 0 && group.d < Math.max(1, delays.length))) group.d = pick(Math.max(1, delays.length));
			}
			const title = titles[group.t] || titles[0];
			const delay = delays.length ? delays[group.d] || delays[0] : null;

			if (title.text) {
				const heading = this.querySelector(".product-popup-modal--heading");
				if (heading) heading.textContent = title.text;
			}
			if (title.button) {
				// Text-label submit button only (the popup always renders one); the loading spinner span is left alone
				const label = this.querySelector("button.submit > span:not(.signup-submit-loading)");
				if (label) label.textContent = title.button;
			}
			if (delays.length > 1 && delay) this.delay = delay * 1000;

			const params = { ab_test: name, ab_title: title.label };
			if (delays.length > 1 && delay) params.ab_delay = delay + "s";
			this.abParams = params;

			// lead_form_view reads every data-ga4-* on this element, so the shown event carries the group too
			Object.keys(params).forEach((key) => {
				this.setAttribute("data-ga4-" + key.replace(/_/g, "-"), params[key]);
			});
			// Shopify customer tags -> subscriptions countable per group in Shopify / Klaviyo,
			// which is the source of truth (GA4 subscribe_success under-counts).
			const tagsInput = this.querySelector('input[name="contact[tags]"]');
			if (tagsInput) {
				const extra = [name + "-title-" + params.ab_title];
				if (params.ab_delay) extra.push(name + "-delay-" + params.ab_delay);
				tagsInput.value = [tagsInput.value].concat(extra).filter(Boolean).join(",");
			}
			if (!preview) {
				// params is stored too: the subscribe_success snippet only has storage to go on after the redirect
				group.params = params;
				try {
					window.localStorage.setItem(POPUP_AB_KEY, JSON.stringify(group));
				} catch (e) {}
			}
			if (!preview && typeof window.__soulogGa4Event === "function") {
				window.__soulogGa4Event("popup_ab_assign", Object.assign({}, params));
			}
		}

		/**
		 * Conditions for auto-show: data-delay has elapsed, and the user has really interacted (click / keypress / wheel / touch).
		 *
		 * Why wait for interaction: the popup's left image is larger than the first-screen hero, so showing it after 8 seconds makes the browser record it as the LCP instead.
		 * Measured on 2026-09-18: home page mobile LCP went from 3.2s to 9.7s, score dropped from 87 to 62; when a real visitor stays
		 * idle for 8 seconds, this is also the number reported to CrUX. The browser stops tracking LCP after the user's first interaction,
		 * so by waiting until after interaction to show, the popup won't be counted as the LCP.
		 * See the Lighthouse re-test report docs/performance/2026-09-18-lighthouse-*.md
		 *
		 * Why buffer 1 second after the interaction: if the popup appears at the moment of pointerdown, the pointerup of the same click
		 * lands on the overlay, the synthesized click hits the overlay -> hide() -> records a 14-day cooldown, and the user is blocked for two weeks
		 * before ever seeing the popup. Visitors who interacted before the 8 seconds are unaffected; it shows right on time.
		 */
		scheduleAutoShow() {
			let elapsed = false;
			let interacted = false;
			const listenerOptions = { capture: true, passive: true };
			// Re-check right before showing: the visitor may have opened it by hand in the meantime (e.g. the buy bar's
			// offer tag) and closed it -- the pending auto-show must not pop it up a second time.
			const showIfStillWanted = () => {
				if (this.hasAttribute("open") || !this.shouldAutoShow()) return;
				this.show();
			};
			const tryShow = (wait) => {
				if (!elapsed || !interacted) return;
				if (wait) setTimeout(showIfStillWanted, wait);
				else showIfStillWanted();
			};
			const onInteract = () => {
				if (interacted) return;
				interacted = true;
				POPUP_INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, onInteract, listenerOptions));
				tryShow(POPUP_AFTER_INTERACTION_MS);
			};
			POPUP_INTERACTION_EVENTS.forEach((type) => window.addEventListener(type, onInteract, listenerOptions));
			setTimeout(() => {
				elapsed = true;
				tryShow(0);
			}, this.delay);
		}

		/**
		 * Download the popup's left image in advance during the "wait 8 seconds" window.
		 *
		 * The image goes through lazysizes, and lazySizesConfig.loadHidden = false -- while the popup is hidden it
		 * doesn't fetch the image at all; the download only starts the moment it pops up, so the user stares at a 20px blurry placeholder for quite a while.
		 * Here, after the page load finishes and the browser is idle, data-srcset is manually applied as srcset, without competing with the first screen for bandwidth.
		 */
		schedulePreload() {
			if (typeof window.addEventListener !== "function") return;
			const run = () => {
				if (typeof window.requestIdleCallback === "function") {
					window.requestIdleCallback(() => this.preloadImages(), { timeout: 1500 });
				} else {
					this.preloadImages();
				}
			};
			if (document.readyState === "complete") run();
			else window.addEventListener("load", run, { once: true });
		}

		preloadImages() {
			if (this.imagesPreloaded) return;
			this.imagesPreloaded = true;
			this.querySelectorAll(".product-popup-modal__content-image img.lazyload[data-srcset]").forEach((img) => {
				// While hidden, data-sizes="auto" can't measure a width, so hard-code it per the layout in overlay-group.css:
				// on desktop the image takes the left half of the popup (≤720px) ≈ 360px; on phones it fills calc(100vw - 30px).
				img.sizes = "(min-width: 768px) 360px, calc(100vw - 30px)";
				// Switch to lazyloading: keeps the blur transition styles while leaving lazysizes' watch queue (it only recognizes .lazyload)
				img.classList.remove("lazyload");
				img.classList.add("lazyloading");
				img.addEventListener(
					"load",
					() => {
						img.classList.remove("lazyloading");
						img.classList.add("lazyloaded");
					},
					{ once: true },
				);
				img.srcset = img.dataset.srcset;
			});
		}

		show(opener) {
			this.preloadImages();
			this.openedBy = opener;
			document.body.classList.add("overflow-hidden");
			this.setAttribute("open", "");

			// GA4 lead_form_view: only now does it count as "popup shown". Can't hand this to IntersectionObserver --
			// the popup is hidden via opacity/visibility and is full-screen fixed, so IO would wrongly judge it visible the instant the page loads
			// (see the comment in sections/overlay-popup.liquid). Only popups marked with data-ga4-view
			// actually report; other uses (video popup / size chart, etc.) return immediately when calling in.
			if (typeof window.__soulogGa4View === "function") {
				window.__soulogGa4View(this);
			}

			setTimeout(() => {
				this.querySelector('[role="dialog"]').focus();
			}, 100);
		}

		hide() {
			document.body.classList.remove("overflow-hidden");
			this.removeAttribute("open");
			this.querySelectorAll(".js-youtube").forEach((video) => {
				video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
			});
			this.querySelectorAll(".js-vimeo").forEach((video) => {
				video.contentWindow.postMessage('{"method":"pause"}', "*");
			});
			this.querySelectorAll("video").forEach((video) => video.pause());

			// Closing enters the cooldown period. All three ways of closing (close button / ESC / clicking the overlay) funnel into hide(),
			// so writing it once here is enough -- previously only the close button was bound to setCookie, so people who pressed ESC / clicked the overlay
			// got the popup again on every visit.
			if (this.popup) {
				this.writeSuppression({ closedAt: new Date().toISOString() });
			}
		}

		/**
		 * Do-not-disturb state for the auto popup. Shape: { closedAt: ISO string, subscribed: true }.
		 *
		 * Backward compatibility with the old format: before the fix, JSON.stringify(new Date()) (a plain date string) was written,
		 * and the code only checked "whether the key exists", never looking at the date -- anyone who clicked the close button was effectively blocked forever.
		 * Here the old value is read back as closedAt, so they will see the popup again after the 14-day cooldown.
		 */
		readSuppression() {
			let raw = null;
			try {
				raw = window.localStorage.getItem(this.id);
			} catch (e) {
				return {};
			}
			if (!raw) return {};
			let value = null;
			try {
				value = JSON.parse(raw);
			} catch (e) {
				return {};
			}
			if (typeof value === "string") return { closedAt: value };
			return value && typeof value === "object" ? value : {};
		}

		writeSuppression(patch) {
			const next = Object.assign(this.readSuppression(), patch);
			try {
				window.localStorage.setItem(this.id, JSON.stringify(next));
			} catch (e) {}
		}

		shouldAutoShow() {
			const state = this.readSuppression();
			if (state.subscribed) return false;
			if (!state.closedAt) return true;
			const closedAt = Date.parse(state.closedAt);
			// If the timestamp can't be parsed (hand-edited / old dirty data), treat it as "never closed"; better to show once more than to block forever.
			if (Number.isNaN(closedAt)) return true;
			return Date.now() - closedAt >= POPUP_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
		}

		/**
		 * Successful subscription = permanent do-not-disturb.
		 *
		 * Shopify's customer form does a full-page POST and redirects back with ?customer_posted=true;
		 * the page may also contain other subscribe forms such as the footer's, and the Liquid layer can't tell which one submitted --
		 * so at the moment of submission a sessionStorage marker is set inside this popup, and it is settled on redirect back.
		 */
		watchSubscribeSuccess() {
			const pendingKey = this.id + ":pending";
			this.addEventListener(
				"submit",
				(event) => {
					const form = event.target;
					if (!form || !form.classList || !form.classList.contains("signup-form")) return;
					try {
						window.sessionStorage.setItem(pendingKey, "1");
						// The group this visitor actually saw, for subscribe_success after the redirect
						// (snippets/ga4-subscribe-result.liquid). The stored natural group isn't enough:
						// a ?popup_ab= preview shows another arm without storing it, and the tags already follow the page.
						window.sessionStorage.setItem(POPUP_AB_KEY + "_submit", JSON.stringify(this.abParams || null));
					} catch (e) {}
				},
				true,
			);

			let posted = false;
			try {
				posted = new URLSearchParams(window.location.search).has("customer_posted");
			} catch (e) {}
			if (!posted) return;

			let pending = false;
			try {
				pending = window.sessionStorage.getItem(pendingKey) === "1";
				window.sessionStorage.removeItem(pendingKey);
			} catch (e) {}
			if (pending) this.writeSuppression({ subscribed: true });
		}

		// Only the age verification popup still uses this (its "once confirmed, never ask again" should never expire anyway).
		// The auto-show subscribe popup now goes through readSuppression / writeSuppression above.
		getCookie() {
			return window.localStorage.getItem(this.id);
		}
		setCookie() {
			window.localStorage.setItem(this.id, JSON.stringify(new Date()));
		}
	}
	customElements.define("modal-dialog", ModalDialog);
}
if (!customElements.get("modal-opener")) {
	class ModalOpener extends HTMLElement {
		constructor() {
			super();

			const button = this.querySelector("button");

			if (!button) return;
			button.addEventListener("click", () => {
				const modal = document.querySelector(this.getAttribute("data-modal"));
				if (modal) modal.show(button);
			});
		}
	}
	customElements.define("modal-opener", ModalOpener);
}
